import {
    Delete,
    MoreVert,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { FilterSection } from 'src/components/SetsPlayground/FilterSection';
import { NumericInput } from 'src/components/SetsPlayground/NumericInputField';
import { DistributionWidgetContainer } from 'src/components/SetsPlayground/Widgets/DistributionWidgetContainer';
import { RankingWidgetContainer } from 'src/components/SetsPlayground/Widgets/RankingWidgetContainer';
import { SemanticRankingWidget } from 'src/components/SetsPlayground/Widgets/SemanticRankingWidget';
import { ClassifierRankingWidget } from 'src/components/SetsPlayground/Widgets/ClassifierRankingWidget';
import { WidgetOption } from 'src/components/SetsPlayground/Widgets/WidgetSelector';
import { DesignConceptInlineRankingWidget } from './DesignConceptInlineRankingWidget';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetRanking = Models.SetsPlayground.SetRanking;

const SetItem = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `${theme.spacing(1)} solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    gap: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(-4),
}));

const SET_CONFIG_RANKING_WIDGETS: WidgetOption<SetRanking>[] = [
    DesignConceptInlineRankingWidget as WidgetOption<SetRanking>,
    SemanticRankingWidget as WidgetOption<SetRanking>,
    ClassifierRankingWidget as WidgetOption<SetRanking>,
];

const SET_ACCENT_COLORS = [
    '#0075C2',
    '#e6bc49',
] as const;

export interface SetEditorCardProps {
    set: SetDefinition;
    displayIndex: number;
    productKey: string;
    cultures: string[];
    hasDuplicateName?: boolean;
    hasReservedName?: boolean;
    validationErrors?: string[];
    onChange: (update: Partial<SetDefinition>) => void;
    onRemove: () => void;
}

export const SetEditorCard = ({
    set,
    displayIndex,
    productKey,
    cultures,
    hasDuplicateName = false,
    hasReservedName = false,
    validationErrors = [],
    onChange,
    onRemove,
}: SetEditorCardProps): JSX.Element => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const { ranking } = set;
    const isInlineRanking = ranking.type === 'inline';

    const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
        setMenuAnchorEl(null);
    };

    const handleDeleteSet = (): void => {
        handleMenuClose();
        onRemove();
    };

    const commitPriority = (value: number | undefined): void => {
        if (typeof value === 'number') {
            onChange({ priority: value });
        }
    };

    let nameHelperText: string | undefined;

    if (hasReservedName) {
        nameHelperText = '"newest" is a reserved name.';
    } else if (hasDuplicateName) {
        nameHelperText = 'Set name must be unique.';
    }

    return (
        <SetItem sx={{ borderLeftColor: SET_ACCENT_COLORS[displayIndex % SET_ACCENT_COLORS.length] }}>
            <Box alignItems="center" display="flex" gap={2}>
                <NumericInput
                    required
                    label="Priority"
                    min={1}
                    value={set.priority}
                    onBlur={commitPriority}
                />
                <TextField
                    fullWidth
                    required
                    error={hasDuplicateName || hasReservedName}
                    helperText={nameHelperText}
                    label="Name"
                    placeholder={`Set ${displayIndex + 1}`}
                    size="small"
                    value={set.name}
                    onChange={(e): void => onChange({ name: e.target.value })}
                />
                <Chip
                    color={isInlineRanking ? 'primary' : 'default'}
                    label={isInlineRanking ? 'Inline ranking' : 'Default'}
                    size="small"
                />
                <IconButton aria-label="set actions" onClick={handleMenuOpen}>
                    <MoreVert />
                </IconButton>
            </Box>
            <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleMenuClose}>
                <MenuItem onClick={handleDeleteSet}>
                    <ListItemIcon>
                        <Delete color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: 'error' }}>
                        Delete set
                    </ListItemText>
                </MenuItem>
            </Menu>
            {validationErrors.length > 0 && (
                <Alert severity="error">
                    {validationErrors.map((message) => (
                        <div key={message}>{message}</div>
                    ))}
                </Alert>
            )}
            <SectionHeading variant="body1">
                Distribution
            </SectionHeading>
            <Divider />
            <DistributionWidgetContainer
                index={0}
                set={set}
                onChangeDistribution={(_index, distribution): void => onChange({ distribution })}
            />

            <SectionHeading variant="body1">
                Ranking
            </SectionHeading>
            <Divider />
            <RankingWidgetContainer
                index={0}
                set={set}
                widgetProps={{ productKey, cultures }}
                widgets={SET_CONFIG_RANKING_WIDGETS}
                onChangeRanking={(_index, nextRanking): void => onChange({ ranking: nextRanking })}
            />

            <FilterSection
                showApproximateResultWarning
                showTitle
                filter={set.filters}
                productKey={productKey}
                onChangeFilter={(filters): void => onChange({ filters })}
            />
        </SetItem>
    );
};
